import { useCallback } from "react";
import type { AriaAttributes, KeyboardEvent } from "react";

interface TableSortHeaderProps {
  label: string;
  field: string;
  currentField: string;
  currentDir: "asc" | "desc";
  onSort: (field: string, dir: "asc" | "desc") => void;
  className?: string;
  align?: "left" | "right" | "center";
}

export default function TableSortHeader({
  label,
  field,
  currentField,
  currentDir,
  onSort,
  className = "",
  align = "left",
}: TableSortHeaderProps) {
  const isActive = currentField === field;

  const handleSort = useCallback(() => {
    if (!isActive) {
      onSort(field, "desc");
    } else if (currentDir === "desc") {
      onSort(field, "asc");
    } else {
      onSort("", "desc");
    }
  }, [isActive, currentDir, field, onSort]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTableCellElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleSort();
      }
    },
    [handleSort]
  );

  const alignClass =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";

  const ariaSort: AriaAttributes["aria-sort"] = isActive
    ? currentDir === "asc"
      ? "ascending"
      : "descending"
    : "none";

  const nextDirLabel = !isActive
    ? "降序"
    : currentDir === "desc"
      ? "升序"
      : "取消";

  return (
    <th
      role="button"
      tabIndex={0}
      aria-sort={ariaSort}
      onClick={handleSort}
      onKeyDown={handleKeyDown}
      title={`按 ${label} ${nextDirLabel} 排列`}
      className={`${alignClass} px-3 md:px-5 py-3 md:py-3.5 text-xs font-medium cursor-pointer select-none hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 rounded-sm transition-colors ${
        isActive ? "text-brand" : "text-text-secondary"
      } ${className}`}
    >
      <div className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <span>{label}</span>
        <div className="flex flex-col -space-y-0.5" aria-hidden="true">
          <i
            className={`ri-arrow-up-s-fill text-[10px] leading-none ${
              isActive && currentDir === "asc" ? "text-brand" : "text-text-muted/40"
            }`}
          />
          <i
            className={`ri-arrow-down-s-fill text-[10px] leading-none ${
              isActive && currentDir === "desc" ? "text-brand" : "text-text-muted/40"
            }`}
          />
        </div>
      </div>
    </th>
  );
}